import React from 'react'
import { useLanguage } from '../LanguageContext'

/* ─────────────────────────────────────────────────────────────
   Sidebar
   Props:
     activeSection  string
     collapsed      bool
     isMobile       bool
     drawerOpen     bool
     onNavigate     fn(id)
     onCollapse     fn()
     onSignOut      fn()
     member         { name, initials, memberId, avatarUrl }
                    avatarUrl — full URL string or null/undefined
                    When null/undefined the initials are shown instead.
───────────────────────────────────────────────────────────── */

const NAV_LABEL_KEY = {
  aviation:  'navFlightArrangements',
  hotels:    'navPartnerHotels',
  transfers: 'navCarTransfers',
  airport:   'navAirportServices',
  about:     'navAbout',
  blog:      'navBlog',
}

const NAV_ITEMS = [
  { id: 'aviation' },
  { id: 'hotels' },
  { id: 'transfers' },
  { id: 'airport' },
  { id: 'about' },
  { id: 'blog' },
]

export { NAV_ITEMS }

export default function Sidebar({
  activeSection,
  collapsed,
  isMobile,
  drawerOpen,
  onNavigate,
  onCollapse,
  onSignOut,
  member,
}) {
  const { t } = useLanguage()

  const sidebarClass = [
    'db-sidebar',
    collapsed && !isMobile ? 'db-sidebar--slim' : '',
    isMobile               ? 'db-sidebar--mobile' : '',
    isMobile && drawerOpen ? 'db-sidebar--open' : '',
  ].filter(Boolean).join(' ')

  return (
    <aside className={sidebarClass} aria-label={t('mainNavigation')}>

      {/* ── Brand ── */}
      <div className="db-sidebar__brand">
        <div className="db-sidebar__brand-text">
          <img src="/images/logo.png" alt={t('logoAlt')} />
        </div>
        {!isMobile && (
          <button
            className="db-sidebar__collapser"
            onClick={onCollapse}
            aria-label={collapsed ? t('expandSidebar') : t('collapseSidebar')}
          >
            <CollapseIcon collapsed={collapsed} />
          </button>
        )}
      </div>

      {/* ── Navigation ── */}
      <nav className="db-nav">
        <ul className="db-nav__list" role="list">

          {/* Home */}
          <li>
            <button
              className={`db-nav__btn${activeSection === 'home' ? ' db-nav__btn--active' : ''}`}
              onClick={() => onNavigate('home')}
              title={collapsed ? t('navHome') : undefined}
            >
              <span className="db-nav__icon"><IconHome /></span>
              {!collapsed && <span className="db-nav__label">{t('navHome')}</span>}
            </button>
          </li>

          {/* Request History */}
          <li>
            <button
              className={`db-nav__btn${activeSection === 'history' ? ' db-nav__btn--active' : ''}`}
              onClick={() => onNavigate('history')}
              title={collapsed ? t('navRequestHistory') : undefined}
            >
              <span className="db-nav__icon"><IconHistory /></span>
              {!collapsed && <span className="db-nav__label">{t('navRequestHistory')}</span>}
            </button>
          </li>

          {!collapsed && <li className="db-nav__divider" aria-hidden="true" />}

          {NAV_ITEMS.map(item => (
            <li key={item.id}>
              <button
                className={`db-nav__btn${activeSection === item.id ? ' db-nav__btn--active' : ''}`}
                onClick={() => onNavigate(item.id)}
                title={collapsed ? t(NAV_LABEL_KEY[item.id]) : undefined}
              >
                <span className="db-nav__icon"><NavIcon id={item.id} /></span>
                {!collapsed && <span className="db-nav__label">{t(NAV_LABEL_KEY[item.id])}</span>}
              </button>
            </li>
          ))}

        </ul>
      </nav>

      {/* ── Sidebar footer ── */}
      <div className="db-sidebar__foot">

        {/* Member chip → profile */}
        <button
          className={`db-member db-member--btn${activeSection === 'profile' ? ' db-member--active' : ''}`}
          onClick={() => onNavigate('profile')}
          title={collapsed ? t('myProfile') : undefined}
          aria-label={t('viewMyProfile')}
        >
          {/* Avatar: show image when available, fall back to initials */}
          <MemberAvatar avatarUrl={member.avatarUrl} initials={member.initials} />

          {!collapsed && (
            <div className="db-member__text">
              <span className="db-member__name">{member.name}</span>
              <span className="db-member__id">{member.memberId}</span>
            </div>
          )}
        </button>

        {/* Sign out */}
        <button
          className="db-signout"
          onClick={onSignOut}
          aria-label={t('signOut')}
          title={t('signOut')}
        >
          <IconSignOut />
          {!collapsed && <span>{t('signOut')}</span>}
        </button>

      </div>

    </aside>
  )
}

/* ─────────────────────────────────────────────────────────────
   MemberAvatar
   Renders a circular avatar that shows:
     • the member's photo  — when avatarUrl is a non-empty string
     • the member's initials (text) — when avatarUrl is null/undefined/''
   Uses a key on the <img> so React replaces the element entirely
   whenever the URL changes, preventing stale-src flicker.
───────────────────────────────────────────────────────────── */
function MemberAvatar({ avatarUrl, initials }) {
  if (avatarUrl) {
    return (
      <div className="db-member__avatar db-member__avatar--img">
        <img
          key={avatarUrl}
          src={avatarUrl}
          alt=""
          aria-hidden="true"
          className="db-member__avatar-img"
        />
      </div>
    )
  }
  return (
    <div className="db-member__avatar">
      {initials}
    </div>
  )
}

/* ── Icons ── */
const SV = { fill: 'none', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round' }

function IconHome() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2 7L8 2L14 7V14H10V10H6V14H2V7Z" {...SV} strokeWidth="0.9" />
    </svg>
  )
}

function IconHistory() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeWidth="0.85" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M7.5 4.5V7.5L9.5 9.5" stroke="currentColor" strokeWidth="0.85" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function IconSignOut() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M5 2H2a1 1 0 00-1 1v8a1 1 0 001 1h3M10 10l3-3m0 0l-3-3m3 3H5" {...SV} strokeWidth="1" />
    </svg>
  )
}

function CollapseIcon({ collapsed }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d={collapsed ? 'M4 2l4 4-4 4' : 'M8 2L4 6l4 4'} {...SV} strokeWidth="1" />
    </svg>
  )
}

export function NavIcon({ id }) {
  const p = { width: '15', height: '15', viewBox: '0 0 15 15', fill: 'none' }
  const s = { ...SV, strokeWidth: '0.85' }
  switch (id) {
    case 'aviation':
      return <svg {...p}><path d="M13 4L9 7.5H13L10.5 12.5H8.5L7 9L2 11.5V9.5L6 7L2 3V1L7 4L8.5 0H10.5L13 4Z" {...s} /></svg>
    case 'hotels':
      return <svg {...p}><rect x="2" y="6" width="11" height="8" rx="0.5" {...s} /><path d="M4 14V11H6V14M8.5 11H11V13H8.5V11Z" {...s} /><path d="M2 6L7.5 2L13 6" {...s} /></svg>
    case 'transfers':
      return <svg {...p}><rect x="1" y="6" width="13" height="5" rx="1" {...s} /><path d="M3 6L4.5 3H10.5L12 6" {...s} /><circle cx="4" cy="12.5" r="1.5" {...s} /><circle cx="11" cy="12.5" r="1.5" {...s} /></svg>
    case 'airport':
      return <svg {...p}><path d="M2 13H13M7.5 2.5V9.5M5 7L7.5 9.5L10 7" {...s} /></svg>
    case 'about':
      return <svg {...p}><circle cx="7.5" cy="7.5" r="5.5" {...s} /><path d="M7.5 6.5V10.5M7.5 5V5.5" {...{ ...s, strokeWidth: '1' }} /></svg>
    case 'blog':
      return <svg {...p}><rect x="2" y="2" width="11" height="11" rx="1" {...s} /><path d="M4.5 5H10.5M4.5 7.5H8.5M4.5 10H7" {...s} /></svg>
    default:
      return null
  }
}